import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import { UserIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import { CallTranscript } from "shared/components/CallTranscript";
import { TeamTriedFirstBadge } from "shared/components/TeamTriedFirstBadge";
import { aiHandoffReasonText } from "shared/utils/ai-handoff";
import { useCallPlayback } from "shared/hooks/use-call-playback";
import { formatCallDuration } from "shared/utils/missed-call";
import { MessageContentCard } from "./MessageContentCard";
import { TranscriptToggle } from "./TranscriptToggle";
import { MessageAddressField } from "./MessageAddressField";
import type { NoteDetails } from "../types";
import { inboxApi } from "../api/api";
import agentProfileSvg from "shared/icons/boomLeasingAgentProfile.svg";

interface CallTranscriptContentProps {
  // AI-leasing-agent path: transcript fetched via note's call_transcript_id.
  noteDetails?: NoteDetails;
  // Outbound softphone path: transcript fetched via the BoomCRM call id.
  outboundCallId?: string;
  contactName?: string;
  isFromContact?: boolean;
  isInbound?: boolean;
  // Gate the transcript fetch — defaults to on for the existing always-open
  // (AI-agent) usage; the inbox voice card passes `false` until the card is
  // expanded so a thread of completed calls doesn't fire N transcript GETs.
  enabled?: boolean;
}

export const CallTranscriptContent = ({
  noteDetails,
  outboundCallId,
  contactName = "Unknown",
  isFromContact = true,
  isInbound,
  enabled = true
}: CallTranscriptContentProps) => {
  // The AI agent's card is the caller's incoming call, so it takes the
  // inbound (white) ground even though the note itself is system-authored.
  const usesInboundColor = noteDetails ? true : (isInbound ?? isFromContact);
  const [showTranscript, setShowTranscript] = useState(false);
  // Ties the recording below to the transcript at the bottom of this card.
  const playback = useCallPlayback();
  const { data, isLoading: loading } = useQuery({
    queryKey: outboundCallId
      ? ["OUTBOUND_CALL_TRANSCRIPT", outboundCallId]
      : ["CALL_TRANSCRIPT", noteDetails?.call_transcript_id],
    queryFn: () =>
      outboundCallId
        ? inboxApi.getOutboundCallTranscript(outboundCallId)
        : inboxApi.getCallTranscript(noteDetails!.call_transcript_id!),
    enabled: enabled && (!!outboundCallId || !!noteDetails?.call_transcript_id)
  });

  const transcript = data?.transcript ?? [];
  const routeText = aiHandoffReasonText(
    noteDetails?.team_first_route,
    noteDetails?.handed_off_by_name
  );
  const duration = formatCallDuration(data?.recording_duration_seconds);
  const fromNumber = noteDetails?.from_number || data?.caller_phone_number;
  const toNumber = noteDetails?.to_number || data?.agent_phone_number;

  const transcriptToggle = (
    <TranscriptToggle
      open={showTranscript}
      onToggle={() => setShowTranscript((open) => !open)}
      hidden={transcript.length === 0}
    />
  );

  if (loading)
    return (
      <MessageContentCard
        isFromContact={isFromContact}
        usesInboundColor={usesInboundColor}
      >
        <p className="text-sm text-content-200">Loading transcript...</p>
      </MessageContentCard>
    );

  return (
    <MessageContentCard
      isFromContact={isFromContact}
      usesInboundColor={usesInboundColor}
      className="flex w-full flex-col gap-4 divide-y divide-alpha-40 [&>*]:pt-4 [&>*:first-child]:pt-0"
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <img src={agentProfileSvg} alt="" className="size-5 rounded-full" />
          <span className="text-sm font-bold leading-5 text-content-1000">
            Incoming call · answered by the AI agent
          </span>
          <Show if={!!routeText}>
            <TeamTriedFirstBadge />
          </Show>
          <Show if={!!duration}>
            <span className="ml-auto text-[13px] font-medium leading-4 tabular-nums text-content-400">
              {duration}
            </span>
          </Show>
        </div>
        <Show if={!!fromNumber || !!toNumber}>
          <div className="flex flex-wrap items-center gap-2">
            <Show if={!!fromNumber}>
              <MessageAddressField
                label="From:"
                address={{ name: contactName, address: fromNumber ?? "" }}
              />
            </Show>
            <Show if={!!toNumber}>
              <MessageAddressField
                label="To:"
                address={{ name: "", address: toNumber ?? "" }}
                isLine
              />
            </Show>
          </div>
        </Show>
      </div>

      <div className="flex flex-col gap-3">
        <Show if={!!routeText}>
          <div className="flex items-center gap-2 text-[13px] font-medium leading-4 text-content-400">
            <HugeiconsIcon icon={UserIcon} size={16} />
            <span>{routeText}</span>
          </div>
        </Show>

        {data?.recording_url ? (
          <CallRecordingPlayer
            recordingUrl={data.recording_url}
            durationSeconds={data.recording_duration_seconds}
            playback={playback}
            trailing={transcriptToggle}
          />
        ) : (
          <div>{transcriptToggle}</div>
        )}

        <Show if={showTranscript}>
          <CallTranscript
            lines={transcript}
            contactName={contactName}
            playback={playback}
            previewLines={transcript.length}
            hideHeader
            boxed
          />
        </Show>
      </div>
    </MessageContentCard>
  );
};
